import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import VerifyForm from './components/VerifyForm';
import Portal from './components/Portal';
import { PortalLoginConsume, PortalLoginRequest } from './components/PortalLogin';
import {
  CustomerAccess,
  CustomerDashboard,
  CustomerEmailChangeConfirm,
  CustomerForgotPassword,
  CustomerLogin,
  CustomerResetPassword,
  ExistingCustomerActivationConfirm,
  ExistingCustomerRegistration,
  ExistingCustomerInvitationConfirm,
  PaymentAccessConfirm,
  PaymentAccessRequest,
} from './components/CustomerAccount';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import Feedback from './components/Feedback';
import GoogleReviewSummary from './components/GoogleReviewSummary';
import LogisticsForm from './components/LogisticsForm';
import PremiumOffer from './components/PremiumOffer';
import MailboxDisplay from './components/MailboxDisplay';
import StripeCheckoutCompletion, { StripeCheckoutCancelled } from './components/StripeCheckoutCompletion';
import AppErrorBoundary from './components/AppErrorBoundary';
import { getLang, setLang, t } from './i18n';


function LanguageSwitcher() {
  const activeLang = getLang();
  if (window.location.pathname.startsWith('/display/mailbox')) return null;

  return (
    <div
      className="language-switcher"
      role="group"
      aria-label={activeLang === 'de' ? 'Sprache wählen' : 'Choose language'}
    >
      <button
        type="button"
        onClick={() => setLang('de')}
        aria-pressed={activeLang === 'de'}
        style={{
          fontWeight: activeLang === 'de' ? 700 : 400,
        }}
      >
        {t('language_de')}
      </button>
      <button
        type="button"
        onClick={() => setLang('en')}
        aria-pressed={activeLang === 'en'}
        style={{
          fontWeight: activeLang === 'en' ? 700 : 400,
        }}
      >
        {t('language_en')}
      </button>
    </div>
  );
}

function App() {
  React.useEffect(() => {
    const activeLang = getLang();
    localStorage.setItem('lang', activeLang);
    document.documentElement.lang = activeLang;
  }, []);

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <a className="skip-link" href="#main-content-anchor">
          {getLang() === 'en' ? 'Skip to main content' : 'Zum Hauptinhalt springen'}
        </a>
        <span id="main-content-anchor" className="skip-target" tabIndex="-1" />
        <LanguageSwitcher />
        <Routes>
        <Route
          path="/"
          element={
            <main className="page-shell">
              <div className="brand-header">
                <div>
                  <p className="eyebrow">YourGermanAddress</p>
                  <h1>{t('portal_title')}</h1>
                  <p className="subtitle">
                    {t('portal_home_subtitle')}
                  </p>
                </div>
              </div>

              <div className="card">
                <p>{t('portal_home_info')}</p>
                <p>
                  <a className="primary-link-button" href="/account/login">
                    {getLang() === 'en' ? 'Open Business Hub' : 'Business Hub öffnen'}
                  </a>
                </p>
              </div>
              <GoogleReviewSummary />
            </main>
          }
        />
        <Route path="/verify/:token" element={<VerifyForm />} />
        <Route path="/account/login" element={<CustomerLogin />} />
        <Route path="/account/activate-existing" element={<ExistingCustomerRegistration />} />
        <Route path="/account/activate-existing/:claimToken" element={<ExistingCustomerActivationConfirm />} />
        <Route path="/account/invitation/:invitationToken" element={<ExistingCustomerInvitationConfirm />} />
        <Route path="/account/payment-link/:requestToken" element={<PaymentAccessRequest />} />
        <Route path="/account/payment-access/:accessToken" element={<PaymentAccessConfirm />} />
        <Route path="/account/forgot-password" element={<CustomerForgotPassword />} />
        <Route path="/account/reset/:resetToken" element={<CustomerResetPassword />} />
        <Route path="/account/email-change/:changeToken" element={<CustomerEmailChangeConfirm />} />
        <Route path="/account/access/:verificationToken" element={<CustomerAccess />} />
        <Route path="/account/dashboard" element={<CustomerDashboard />} />
        <Route path="/portal" element={<PortalLoginRequest />} />
        <Route path="/portal/login/:loginToken" element={<PortalLoginConsume />} />
        <Route path="/portal/:token" element={<Portal />} />
        <Route path="/logistics/:token" element={<LogisticsForm />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/premium-offer/:token" element={<PremiumOffer />} />
        <Route path="/display/mailbox" element={<MailboxDisplay />} />
        <Route path="/checkout/success" element={<StripeCheckoutCompletion />} />
        <Route path="/checkout/cancelled" element={<StripeCheckoutCancelled />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/scans" element={<AdminDashboard />} />
        <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;


